import { supabase } from '@/integrations/supabase/client';
import type { TablesInsert } from '@/integrations/supabase/types';
import type {
  Notification,
  NotificationPreferences,
  PushToken,
  NotificationReadPort,
  NotificationWritePort,
  NotificationRow,
  NotificationPreferencesRow,
  PushTokenRow,
} from '../core/types';

function mapNotificationRow(row: NotificationRow): Notification {
  return {
    id: row.id,
    userId: row.user_id,
    type: row.type,
    title: row.title,
    body: row.body,
    data: (row.data as Record<string, unknown>) ?? {},
    isRead: row.is_read,
    createdAt: row.created_at,
  };
}

function mapPreferencesRow(row: NotificationPreferencesRow): NotificationPreferences {
  return {
    id: row.id,
    userId: row.user_id,
    waitlistEmail: row.waitlist_email,
    waitlistPush: row.waitlist_push,
    waitlistInApp: row.waitlist_in_app,
    bookingEmail: row.booking_email,
    bookingPush: row.booking_push,
    bookingInApp: row.booking_in_app,
    reminderEmail: row.reminder_email,
    reminderPush: row.reminder_push,
    reminderInApp: row.reminder_in_app,
  };
}

function mapPushTokenRow(row: PushTokenRow): PushToken {
  return {
    id: row.id,
    userId: row.user_id,
    token: row.token,
    platform: row.platform as PushToken['platform'],
  };
}

export const supabaseNotificationReadPort: NotificationReadPort = {
  async getNotifications(userId: string, limit = 50): Promise<Notification[]> {
    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return (data ?? []).map(mapNotificationRow);
  },

  async getUnreadCount(userId: string): Promise<number> {
    const { count, error } = await supabase
      .from('notifications')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId)
      .eq('is_read', false);

    if (error) throw error;
    return count ?? 0;
  },

  async getPreferences(userId: string): Promise<NotificationPreferences | null> {
    const { data, error } = await supabase
      .from('user_notification_preferences')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) throw error;
    return data ? mapPreferencesRow(data) : null;
  },

  async getPushTokens(userId: string): Promise<PushToken[]> {
    const { data, error } = await supabase
      .from('push_tokens')
      .select('*')
      .eq('user_id', userId);

    if (error) throw error;
    return (data ?? []).map(mapPushTokenRow);
  },
};

export const supabaseNotificationWritePort: NotificationWritePort = {
  async createNotification(data: TablesInsert<'notifications'>): Promise<{ id: string }> {
    const { data: row, error } = await supabase
      .from('notifications')
      .insert(data)
      .select('id')
      .single();

    if (error) throw error;
    return { id: row.id };
  },

  async markAsRead(notificationId: string): Promise<void> {
    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('id', notificationId);

    if (error) throw error;
  },

  async markAllAsRead(userId: string): Promise<void> {
    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('user_id', userId)
      .eq('is_read', false);

    if (error) throw error;
  },

  async upsertPreferences(userId: string, prefs: Partial<NotificationPreferences>): Promise<void> {
    const dbPrefs: {
      user_id: string;
      updated_at: string;
      waitlist_email?: boolean;
      waitlist_push?: boolean;
      waitlist_in_app?: boolean;
      booking_email?: boolean;
      booking_push?: boolean;
      booking_in_app?: boolean;
      reminder_email?: boolean;
      reminder_push?: boolean;
      reminder_in_app?: boolean;
    } = {
      user_id: userId,
      updated_at: new Date().toISOString(),
    };

    if (prefs.waitlistEmail !== undefined) dbPrefs.waitlist_email = prefs.waitlistEmail;
    if (prefs.waitlistPush !== undefined) dbPrefs.waitlist_push = prefs.waitlistPush;
    if (prefs.waitlistInApp !== undefined) dbPrefs.waitlist_in_app = prefs.waitlistInApp;
    if (prefs.bookingEmail !== undefined) dbPrefs.booking_email = prefs.bookingEmail;
    if (prefs.bookingPush !== undefined) dbPrefs.booking_push = prefs.bookingPush;
    if (prefs.bookingInApp !== undefined) dbPrefs.booking_in_app = prefs.bookingInApp;
    if (prefs.reminderEmail !== undefined) dbPrefs.reminder_email = prefs.reminderEmail;
    if (prefs.reminderPush !== undefined) dbPrefs.reminder_push = prefs.reminderPush;
    if (prefs.reminderInApp !== undefined) dbPrefs.reminder_in_app = prefs.reminderInApp;

    const { error } = await supabase
      .from('user_notification_preferences')
      .upsert(dbPrefs, { onConflict: 'user_id' });

    if (error) throw error;
  },

  async registerPushToken(userId: string, token: string, platform: PushToken['platform']): Promise<void> {
    const { error } = await supabase
      .from('push_tokens')
      .upsert(
        { user_id: userId, token, platform, updated_at: new Date().toISOString() },
        { onConflict: 'user_id,token' }
      );

    if (error) throw error;
  },

  async removePushToken(token: string): Promise<void> {
    const { error } = await supabase.from('push_tokens').delete().eq('token', token);
    if (error) throw error;
  },
};

/** Subscribe to realtime notification inserts for a user. Returns cleanup function. */
export function subscribeToNotificationInserts(
  userId: string,
  onInsert: () => void,
): () => void {
  const channel = supabase
    .channel(`notifications:${userId}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${userId}`,
      },
      onInsert,
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}
